export type PlatformLimits = 'instagram' | 'linkedin' | 'facebook' | 'x'

export interface CharacterLimitViolation {
  platform: PlatformLimits
  platformName: string
  limit: number
  currentCount: number
  excessCount: number
}

export const PLATFORM_CHARACTER_LIMITS: Record<PlatformLimits, { name: string; limit: number }> = {
  instagram: { name: 'Instagram', limit: 2200 },
  linkedin: { name: 'LinkedIn', limit: 3000 },
  facebook: { name: 'Facebook', limit: 63206 },
  x: { name: 'X', limit: 280 },
}

export const getCharacterCount = (content: string): number => {
  // For very large strings, use the simpler method to avoid memory issues
  // This is a reasonable trade-off since very long content is unlikely to contain
  // complex emoji sequences that would significantly affect the count
  if (content.length > 10000) {
    return [...content].length
  }

  // Use Intl.Segmenter for proper grapheme cluster counting if available
  // This properly handles complex emojis, ZWJ sequences, and other Unicode combinations
  if (typeof Intl !== 'undefined' && Intl.Segmenter) {
    try {
      const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' })
      return Array.from(segmenter.segment(content)).length
    } catch (error) {
      // Fallback if Intl.Segmenter fails (e.g., memory issues)
      return [...content].length
    }
  }

  // Fallback to spread operator for basic emoji support
  // This handles most single emojis but not complex ZWJ sequences
  return [...content].length
}

export const getPlatformDisplayName = (platform: PlatformLimits): string => {
  const platformConfig = PLATFORM_CHARACTER_LIMITS[platform]

  if (platform === 'x') {
    return 'X/Twitter'
  }

  return platformConfig.name
}

export const checkPlatformViolation = (content: string, platform: PlatformLimits): CharacterLimitViolation | null => {
  const currentCount = getCharacterCount(content)
  const platformConfig = PLATFORM_CHARACTER_LIMITS[platform]

  if (currentCount > platformConfig.limit) {
    return {
      platform,
      platformName: platformConfig.name,
      limit: platformConfig.limit,
      currentCount,
      excessCount: currentCount - platformConfig.limit,
    }
  }

  return null
}

export const getCharacterLimitViolations = (
  content: string,
  selectedPlatforms: PlatformLimits[],
): CharacterLimitViolation[] => {
  if (selectedPlatforms.length === 0) {
    return []
  }

  const violations: CharacterLimitViolation[] = []

  for (const platform of selectedPlatforms) {
    const violation = checkPlatformViolation(content, platform)
    if (violation) {
      violations.push(violation)
    }
  }

  return violations
}

export const getStrictestLimit = (selectedPlatforms: PlatformLimits[]): number | null => {
  if (selectedPlatforms.length === 0) {
    return null
  }

  return Math.min(...selectedPlatforms.map((platform) => PLATFORM_CHARACTER_LIMITS[platform].limit))
}
